/**
 * Listing section tab icons.
 * 
 * **Feature-Specific**: Co-located with sticky-header-tabs section.
 * Uses custom SVG icons exported from Figma for the sticky navigation tabs.
 * 
 * Design specs (from Figma node-id=1627-12831):
 * - Desktop: 40px
 * - Mobile: 24px
 * - Icons fade out when tabs become sticky (compact mode)
 * 
 * Icon mapping:
 * - Overview: Custom document/info icon
 * - Price History: Custom upward trend chart
 * - Home Appraisal: Custom house with value indicator
 * - Map: Custom map/location icon
 * - Similar Listings: Custom grid view icon
 * - Commute: Custom transportation/directions icon
 */

import React from 'react';

export interface IconProps {
  size?: number;
  className?: string;
}

// Icon paths - SVGs are served from public directory
const ICON_PATH = '/assets/images/sticky-tabs';

/**
 * Generic SVG icon wrapper for tab icons
 */
function SvgIcon({ src, alt, size = 40, className }: { src: string; alt: string; size?: number; className?: string }) {
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      style={{
        width: `${size}px`,
        height: 'auto',
        display: 'block',
      }}
    />
  );
}

export const OverviewIcon = ({ size = 40, className }: IconProps) => (
  <SvgIcon src={`${ICON_PATH}/overview.svg`} alt="Overview" size={size} className={className} />
);

export const PriceHistoryIcon = ({ size = 40, className }: IconProps) => (
  <SvgIcon src={`${ICON_PATH}/price-history.png`} alt="Price History" size={size} className={className} />
);

export const HomeAppraisalIcon = ({ size = 40, className }: IconProps) => (
  <SvgIcon src={`${ICON_PATH}/home-appraisal.svg`} alt="Home Appraisal" size={size} className={className} />
);

export const MapIcon = ({ size = 40, className }: IconProps) => (
  <SvgIcon src={`${ICON_PATH}/map.svg`} alt="Map" size={size} className={className} />
);

export const SimilarListingsIcon = ({ size = 40, className }: IconProps) => (
  <SvgIcon src={`${ICON_PATH}/similar-listings.svg`} alt="Similar Listings" size={size} className={className} />
);

export const CommuteIcon = ({ size = 40, className }: IconProps) => (
  <SvgIcon src={`${ICON_PATH}/commute.svg`} alt="Commute" size={size} className={className} />
);
